import { Autocomplete, Paper, TextField } from "@mui/material";
import React, { useRef, useState } from "react";
import { useEffect } from "react";

/**
 * MUIAutocomplete Component
 *
 * A reusable Autocomplete component built with Material-UI's Autocomplete, Paper, and TextField components.
 *
 * This component allows users to select one or multiple options from a list of provided `optionsList`. 
   It supports search functionality with a debounced input field and 
   handles the required validation for form submission.
 *
 * Props:
 * - **inputLabel** (string): The label for the autocomplete input field.
 * - **selectedOption** (any): The currently selected option(s), passed down from the parent.
 * - **setSelectedOption** (function): A function to update the selected option(s) from the parent component.
 * - **optionsList** (array): The list of options to display in the dropdown.
 * - **multiple** (boolean): Determines if multiple options can be selected. Defaults to `false`.
 * - **required** (boolean): If `true`, the input will be marked as required. Handles validation for both single and multiple selections.
 * - **name** (string): The name of the field used to match options with selected ones.
 * - **size** (string): The size of the autocomplete input (small, medium, large). Defaults to "medium".
 * - **setCustomSearch** (function): A function to handle the search term (debounced).
 * - **index** (number): Index value for tracking the selected option when multiple instances of the component are used.
 * - **limitTags** (number): The maximum number of selected tags to display in the input. Defaults to 5.
 * - **onBlur** (function): Callback function when the input field loses focus. Defaults to an empty function.
 *
 * Behavior:
 * - The input field allows users to search and filter the options list.
 * - Supports single and multiple selections based on the `multiple` prop.
 * - The component includes a debounce mechanism for search input to minimize unnecessary updates.
 * - The `required` prop triggers validation, and the `array_required` flag ensures validation for both single and multiple selection modes.
 * - If `multiple` is true, the component allows the user to select multiple options. If `false`, only a single option can be selected.
 * - The dropdown list is dynamically filtered based on the current selection(s) and the search query.
 *
 * Notes:
 * - If no options match the search query, an empty dropdown will be displayed.
 * - The `onChange` handler updates the selected option(s) based on user interaction.
 */

export default function MUIAutocomplete({
  inputLabel,
  selectedOption,
  setSelectedOption,
  optionsList,
  multiple,
  required,
  name,
  size,
  setCustomSearch,
  index,
  limitTags,
  onBlur = () => {},
}) {
  const [inputValue, setInputValue] = useState("");
  const [dataList, setDataList] = useState([]);
  const timeoutRef = useRef(null);

  let array_required = false;
  if (required) {
    if (multiple && selectedOption.length == 0) {
      array_required = true;
    } else {
      if (!selectedOption) {
        array_required = true;
      }
    }
  }
  useEffect(() => {
    let date_list = [];
    optionsList.map((item) => {
      if (multiple) {
        let find = selectedOption.find(
          (selected) => selected[name] == item[name]
        );
        if (!find) {
          return date_list.push(item);
        }
      } else if (!selectedOption || selectedOption[name] !== item[name]) {
        return date_list.push(item);
      }
    });
    setDataList(date_list);
  }, []);

  const handleChangeInput = (value) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setCustomSearch(value || "");
    }, 1000);

    setInputValue(value);
  };

  return (
    <Autocomplete
      multiple={multiple}
      value={selectedOption}
      onChange={(event, newValue) => {
        setSelectedOption(newValue, index);
      }}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        handleChangeInput(newInputValue);
      }}
      onBlur={() => setCustomSearch("")}
      className="w-100 mui-select autocomplete-mui"
      PaperComponent={({ children }) => (
        <Paper className="autocomplete-paper">{children}</Paper>
      )}
      size={size ? size : "medium"}
      id="controllable-states-demo"
      options={dataList}
      limitTags={limitTags ? limitTags : 5}
      getOptionLabel={(option) => option[name]}
      renderInput={(params) => {
        const inputProps = params.inputProps;
        inputProps.autoComplete = "off";
        if (array_required) {
          inputProps.required = true;
        }
        return (
          <TextField
            {...params}
            label={inputLabel}
            placeholder={inputLabel}
            required={array_required}
          />
        );
      }}
    />
  );
}
